import { createContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
export const mbUrl = (ibId, path = '') => `${API}/media-buying/${ibId}${path}`;

export const STATUS_LABEL = {
  kajian: 'Kajian', brief_sedia: 'Brief Ready', penghasilan: 'Tengah Produce', sedia_diuji: 'Ready to Test',
  sedang_diuji: 'Tengah Test', semakan: 'Review Result', variasi_seterusnya: 'Next Variation', arkib: 'Arkib',
};
export const BRANCH_LABEL = { ai_self: 'Buat sendiri (AI)', creative_team: 'Minta Creative Team' };
export const TEST_LABEL = { draf: 'Draft', aktif: 'Running', henti: 'Stopped' };
export const REVIEW_LABEL = { belum_disemak: 'Belum review', lulus: 'Lulus', perlu_ubah: 'Kena ubah' };
export const CONN_LABEL = {
  belum_disambungkan: 'Belum connect', disambungkan: 'Connected', token_tamat: 'Token expired',
  permission_tidak_cukup: 'Permission tak cukup', sambungan_gagal: 'Connection gagal',
  disambungkan_mock: 'Connected (mock)', token_tiada: 'Token tak set', demo: 'Mod demo (tiada sambungan)',
};
export const ORIGIN_CLS = { live: 'text-emerald-400', demo_fixture: 'text-amber-400', mock: 'text-amber-400', unknown: 'text-red-400', mixed: 'text-red-400', tiada_data: '' };
export const ORIGIN_LABEL = {
  demo_fixture: 'Data contoh (demo)', mock: 'Data simulasi (mock)', live: 'Data Meta/Telegram sebenar',
  unknown: 'Asal data belum disahkan', mixed: 'Campuran asal data — semak', tiada_data: 'Tiada data lagi',
};
export const fmtMyt = (iso) => (iso ? `${new Date(iso).toLocaleString('en-GB', { timeZone: 'Asia/Kuala_Lumpur' })} MYT` : '—');
export const INVITE_LABEL = {
  tiada: 'Belum ada link', sedang_diproses: 'Tengah process…', berjaya: 'Link ready',
  gagal: 'Gagal', tidak_pasti: 'Tak pasti — check Telegram',
};

export const muted = 'text-[hsl(var(--text-muted))]';
export const primary = 'text-[hsl(var(--text-primary))]';
export const card = 'rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-4';

export const fmtTotals = (totals) => {
  const entries = Object.entries(totals || {});
  if (!entries.length) return '—';
  return entries.map(([cur, v]) => `${cur === '?' ? '(currency ?)' : cur} ${v.amount_spent.toFixed(2)}`).join(' · ');
};

export const errMsg = (e, fallback = 'Permintaan gagal') => {
  const st = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) return 'Sambungan tamat masa — semak internet dan cuba lagi';
  if (!e?.response) return 'Tiada sambungan ke pelayan — semak internet dan cuba lagi';
  if (st === 403) return 'Tiada kebenaran untuk tindakan ni — minta manager/PIC tahap';
  if (st >= 500) return 'Pelayan bermasalah sekejap — cuba lagi dalam beberapa saat';
  return fallback;
};

export const runCall = async (setBusy, fn, okMsg) => {
  setBusy(true);
  try {
    const r = await fn();
    if (okMsg) toast.success(okMsg);
    return r;
  } catch (e) {
    toast.error(errMsg(e));
    return null;
  } finally {
    setBusy(false);
  }
};

export const archiveRec = async (ibId, kind, id, restore = false) => {
  try {
    await axios.post(mbUrl(ibId, `/${kind}/${id}/${restore ? 'restore' : 'archive'}`), {}, auth());
    toast.success(restore ? 'Dipulihkan dari arkib' : 'Diarkibkan — sejarah & link kekal');
    return true;
  } catch (e) {
    toast.error(errMsg(e));
    return false;
  }
};

export const NO_PERM = {
  brief: 'Untuk PIC Brief (Kaer) / manager', creative: 'Untuk PIC Creative (Firzan) / manager', test: 'Untuk PIC Media Buyer (Azrai) / manager',
};

export const store = {
  get: (k) => { try { return window.localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* preference sahaja */ } },
};

export const copyText = async (text) => {
  try {
    if (!navigator.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch { return false; }
};

export const eligibleForTest = (versions) => versions.filter((v) => !v.archived && v.review_status === 'lulus' && /^https:\/\/\S{3,}$/.test(v.asset_link || ''));

export const MbContext = createContext({ accounts: [], members: [], workflows: [] });

export const rm = (v) => (v == null ? 'N/A' : `RM${Number(v).toFixed(2)}`);
export const READY_CLS = (r) => (r?.ok ? 'text-emerald-400' : 'text-amber-400');
export const CTA = {
  lengkapkan_brief: ['Lengkapkan brief', 'ringkasan'], sedia_serah_bahan: ['Serah ke Bahan', 'ringkasan'],
  menunggu_bahan: ['Buka bahan', 'bahan'], perlu_review: ['Semak versi', 'bahan'], sedia_serah_ujian: ['Serah ke Ujian', 'ringkasan'],
  sedia_cipta_ujian: ['Sediakan ujian', 'ujian'], sambungan_belum_lengkap: ['Sediakan ujian', 'ujian'],
  sedia_dinilai: ['Lihat hasil', 'ujian'], arkib: ['Buka butiran', 'ringkasan'],
};
export const isOverdue = (w) => !!w.deadline && !w.archived && w.deadline < new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

export const cpsClass = (v) => (v == null ? '' : v > 8 ? 'text-red-400' : v >= 5 ? 'text-emerald-400' : 'text-sky-400');

export { axios, toast };
